import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import { Description as FileIcon, FolderOpen as FolderIcon, ChevronRight as ChevronRightIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  hover: { scale: 1.03, transition: { duration: 0.2 } },
};

const TeacherResourcesScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { schoolId, teacherId } = state || {};
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedClass, setSelectedClass] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('All');
  const [classes, setClasses] = useState(['All']);
  const [subjects, setSubjects] = useState(['All']);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchResourceCategories();
  }, []);

  useEffect(() => {
    fetchResources();
  }, [selectedClass, selectedSubject, schoolId, teacherId]);

  const fetchResourceCategories = async () => {
    try {
      const { data } = await axios.get('https://kidsm.vercel.app/api/schools/teacher-resources/categories');
      setClasses(['All', ...(data.class || [])]);
      setSubjects(['All', ...(data.subjects || [])]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load resource categories');
    }
  };

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = '/api/schools/teacher-resources';
      if (selectedClass === 'All' && selectedSubject === 'All') {
        // No filtering
      } else if (selectedClass !== 'All' && selectedSubject !== 'All') {
        url = `/api/schools/teacher-resources/filter?class=${encodeURIComponent(selectedClass)}&subject=${encodeURIComponent(selectedSubject)}`;
      } else if (selectedClass !== 'All') {
        url = `/api/schools/teacher-resources/filter?class=${encodeURIComponent(selectedClass)}&subject=All`;
      } else if (selectedSubject !== 'All') {
        url = `/api/schools/teacher-resources/filter?class=All&subject=${encodeURIComponent(selectedSubject)}`;
      }

      console.log('Fetching resources with URL:', url);
      const { data } = await axios.get(`https://kidsm.vercel.app${url}`);

      if (data && Array.isArray(data.resources)) {
        setResources(data.resources);
      } else {
        console.warn('Unexpected response format:', data);
        setResources([]);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError(`Failed to load resources: ${error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchResources();
  };

  if (loading && !refreshing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress color="primary" />
        <Typography sx={{ mt: 2, color: '#6200ee' }}>
          Loading resources...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 8 }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
              Teacher Resources
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label="Class"
                >
                  {classes.map((className) => (
                    <MenuItem key={className} value={className}>
                      {className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label="Subject"
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              {error}
            </Alert>
          )}

          {resources.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <FolderIcon sx={{ fontSize: 50, color: '#999' }} />
              <Typography sx={{ mt: 2, color: '#666', fontSize: '18px' }}>
                No resources found
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {resources.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item._id}>
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <Card
                      sx={{
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        cursor: 'pointer',
                        borderLeft: '4px solid #6200ee',
                      }}
                      onClick={() =>
                        navigate('/teacher/resource-details', {
                          state: { resource: item, schoolId, teacherId },
                        })
                      }
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                        <FileIcon sx={{ color: '#6200ee', mr: 2, fontSize: 30 }} />
                        <Box flexGrow={1}>
                          <Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#666' }}>
                            {item.class} • {item.subject}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '14px', color: '#999', mt: 1 }}
                            noWrap
                          >
                            {item.description || 'No description'}
                          </Typography>
                        </Box>
                        <ChevronRightIcon sx={{ color: '#999' }} />
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default TeacherResourcesScreen;